import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Send, X, Lightbulb, Loader2, MessageCircle, CheckCircle2 } from "lucide-react";

const newSession = () => `mike-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/* Mike — o Guia do Growth League. Janela flutuante, sem histórico para o utilizador:
   cada visita começa limpa e a conversa é arquivada (só o admin a vê) quando fica
   esclarecida ou quando passa para um humano. */
export default function MikeWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [needsHuman, setNeedsHuman] = useState(false);
  const [handingOff, setHandingOff] = useState(false);
  const [session, setSession] = useState(newSession);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, asking]);

  const ask = async (e, forced) => {
    e?.preventDefault?.();
    const text = (forced || input).trim();
    if (!text || asking) return;
    const next = [...messages, { role: "user", text }];
    setMessages(next);
    setInput("");
    setAsking(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/ask/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          session_id: session,
          history: messages.map((m) => ({ role: m.role, text: m.text })),
        }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let finished = false;
      setMessages([...next, { role: "assistant", text: "" }]);
      setAsking(false);
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        if (acc.includes("\u0004")) { finished = true; acc = acc.replaceAll("\u0004", ""); }
        const clean = acc.replaceAll("[HUMANO]", "");
        setMessages([...next, { role: "assistant", text: clean }]);
      }
      if (!finished) throw new Error("incompleta");
      setNeedsHuman(acc.includes("[HUMANO]"));
    } catch {
      setAsking(true);
      try {
        const r = await api.post("/assistant/ask", {
          message: text, session_id: session,
          history: messages.map((m) => ({ role: m.role, text: m.text })),
        });
        setMessages([...next, { role: "assistant", text: r.data.answer || "(sem resposta)" }]);
        setNeedsHuman(!!r.data.needs_human);
      } catch (err) {
        setMessages([...next, { role: "assistant", text: `⚠️ ${formatApiError(err)}` }]);
        setNeedsHuman(true);
      }
      setAsking(false);
    }
  };

  const reset = () => { setMessages([]); setNeedsHuman(false); setSession(newSession()); };

  const resolve = async () => {
    if (messages.length === 0) { setOpen(false); return; }
    try {
      await api.post("/assistant/archive", {
        session_id: session,
        transcript: messages.map((m) => ({ role: m.role, text: m.text })),
        outcome: "resolved",
      });
      toast.success("Conversa fechada. Obrigado!");
    } catch { /* arquivar é silencioso para o utilizador */ }
    reset();
    setOpen(false);
  };

  const talkToHuman = async () => {
    setHandingOff(true);
    try {
      const r = await api.post("/assistant/handoff", {
        session_id: session,
        transcript: messages.map((m) => ({ role: m.role, text: m.text })),
        question: messages.find((m) => m.role === "user")?.text || "",
        page_context: location.pathname,
      });
      setMessages((m) => [...m, {
        role: "assistant",
        text: r.data.notified
          ? `Avisei ${r.data.notified}. Vais ser contactado — a conversa foi guardada para quem te vai ajudar.`
          : "Pedido registado. Alguém da equipa vai falar contigo.",
      }]);
      setNeedsHuman(false);
    } catch (err) { toast.error(formatApiError(err)); }
    setHandingOff(false);
  };

  const toEureka = () => {
    const transcript = messages.map((m) => `${m.role === "user" ? "Eu" : "Mike"}: ${m.text}`).join("\n\n");
    setOpen(false);
    navigate(`/eureka?nova=1&texto=${encodeURIComponent(transcript)}`);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 pr-4 pl-1 py-1 bg-[#0F0F0F] border border-[#333] hover:border-[#E50000] shadow-2xl rounded-full transition-all hover:scale-[1.03]"
          data-testid="mike-fab"
          title="Mike · Guia do Growth League"
        >
          <img src="/mike.jpg" alt="Mike" className="w-11 h-11 rounded-full object-cover" />
          <span className="text-left">
            <span className="block font-display font-black uppercase text-sm tracking-widest leading-none">Mike</span>
            <span className="block text-[9px] uppercase tracking-widest text-[#737373]">pergunta-me</span>
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] bg-[#0A0A0A] border border-[#333] shadow-2xl flex flex-col"
          data-testid="mike-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#222]">
            <div className="flex items-center gap-2 min-w-0">
              <img src="/mike.jpg" alt="Mike" className="w-9 h-9 rounded-full object-cover border border-[#E50000]" />
              <div className="min-w-0">
                <div className="font-display font-black text-sm uppercase tracking-widest">Mike</div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] truncate">Guia do Growth League</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#737373] hover:text-white" data-testid="mike-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-[#737373] text-xs">
                Olá! Sou o Mike. Explico-te as regras e o funcionamento da Liga — pontos, cartões, fases,
                planteis, cargos, EUREKA e candidaturas.
                <div className="mt-3 flex flex-wrap gap-1">
                  {["Quantos coopetidores tem um clube?",
                    "Como funcionam os cartões?",
                    "O que é o EUREKA?",
                    "Quanto vale uma venda?"].map((q) => (
                    <button key={q} onClick={() => ask(null, q)}
                      className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#333] text-[#A3A3A3] hover:text-white hover:border-[#555]"
                      data-testid={`mike-suggestion-${q.slice(0, 6)}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <img src="/mike.jpg" alt="" className="w-6 h-6 rounded-full object-cover mt-0.5 shrink-0" />
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 text-sm ${m.role === "user" ? "bg-[#E50000] text-white" : "bg-[#1A1A1A] text-white border border-[#222]"}`}
                  data-testid={`mike-msg-${m.role}`}
                >
                  {m.text.split("\n").map((line, li) => <div key={li}>{line || "\u00A0"}</div>)}
                </div>
              </div>
            ))}
            {asking && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1A] border border-[#222] px-3 py-2 flex items-center gap-2 text-[#A3A3A3]">
                  <Loader2 className="w-3 h-3 animate-spin" /> o Mike está a escrever…
                </div>
              </div>
            )}
          </div>

          {messages.length > 0 && !asking && (
            <div className="px-3 pb-2 flex flex-wrap gap-2">
              <button onClick={talkToHuman} disabled={handingOff}
                className={`text-[10px] uppercase tracking-widest flex items-center gap-1 px-2 py-1 border transition-colors ${needsHuman ? "border-[#E50000] text-white bg-[#E50000]/15" : "border-[#333] text-[#A3A3A3] hover:text-white"}`}
                data-testid="mike-talk-human">
                {handingOff ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3 h-3" />}
                Falar com o coach
              </button>
              <button onClick={toEureka}
                className="text-[10px] uppercase tracking-widest flex items-center gap-1 px-2 py-1 border border-[#333] text-[#EAB308] hover:text-white"
                data-testid="mike-to-eureka">
                <Lightbulb className="w-3 h-3" /> É uma ideia · EUREKA
              </button>
              <button onClick={resolve}
                className="text-[10px] uppercase tracking-widest flex items-center gap-1 px-2 py-1 border border-[#333] text-[#22C55E] hover:text-white"
                data-testid="mike-resolve">
                <CheckCircle2 className="w-3 h-3" /> Ficou esclarecido
              </button>
            </div>
          )}

          <form onSubmit={ask} className="flex gap-1 p-3 border-t border-[#222]">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pergunta ao Mike…"
              className="kw-input !py-2 !text-sm flex-1"
              data-testid="mike-input"
              autoComplete="off"
            />
            <button type="submit" disabled={asking || !input.trim()} className="kw-btn-primary !py-2 !px-3" data-testid="mike-send">
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
